import { useState, useEffect } from 'react';

export enum Breakpoint {
  XS = 'xs',
  SM = 'sm',
  MD = 'md',
  LG = 'lg',
  XL = 'xl',
}

const breakpoints: Record<Breakpoint, number> = {
  [Breakpoint.XS]: 374,
  [Breakpoint.SM]: 743,
  [Breakpoint.MD]: 949,
  [Breakpoint.LG]: 1439,
  [Breakpoint.XL]: Infinity,
};

export function useBreakpoint(): {
  breakpoint: Record<Breakpoint, boolean>;
  isDesktop: boolean;
} {
  const getBreakpoint = (): Record<Breakpoint, boolean> => {
    if (typeof window === 'undefined') {
      return {
        [Breakpoint.XS]: true,
        [Breakpoint.SM]: false,
        [Breakpoint.MD]: false,
        [Breakpoint.LG]: false,
        [Breakpoint.XL]: false,
      };
    }

    return {
      [Breakpoint.XS]: true,
      [Breakpoint.SM]: window.matchMedia(
        `(min-width: ${breakpoints[Breakpoint.XS] + 1}px)`
      ).matches,
      [Breakpoint.MD]: window.matchMedia(
        `(min-width: ${breakpoints[Breakpoint.SM] + 1}px)`
      ).matches,
      [Breakpoint.LG]: window.matchMedia(
        `(min-width: ${breakpoints[Breakpoint.MD] + 1}px)`
      ).matches,
      [Breakpoint.XL]: window.matchMedia(
        `(min-width: ${breakpoints[Breakpoint.LG] + 1}px)`
      ).matches,
    };
  };

  const [breakpoint, setBreakpoint] =
    useState<Record<Breakpoint, boolean>>(getBreakpoint);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQueries = [
      window.matchMedia(`(min-width: ${breakpoints[Breakpoint.XS] + 1}px)`),
      window.matchMedia(`(min-width: ${breakpoints[Breakpoint.SM] + 1}px)`),
      window.matchMedia(`(min-width: ${breakpoints[Breakpoint.MD] + 1}px)`),
      window.matchMedia(`(min-width: ${breakpoints[Breakpoint.LG] + 1}px)`),
    ];

    const handleMediaChange = () => {
      setBreakpoint(getBreakpoint);
    };

    mediaQueries.forEach(mq => {
      mq.addEventListener('change', handleMediaChange);
    });

    return () => {
      mediaQueries.forEach(mq => {
        mq.removeEventListener('change', handleMediaChange);
      });
    };
  }, []);

  return {
    breakpoint,
    isDesktop: breakpoint[Breakpoint.LG],
  };
}
